import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { toast } from 'react-toastify';

interface MailAyarlari {
  smtpSunucu: string;
  smtpPort: number;
  smtpKullanici: string;
  smtpSifreMasked: string;
  gonderenAd: string;
  gonderenEposta: string;
  sslKullan: boolean;
  gonderimSaati: string;
  aktifMi: boolean;
}

interface Kullanici {
  kullaniciID: number;
  adSoyad: string;
  email: string;
}

interface Alici {
  aliciID: number;
  kullaniciID: number;
  adSoyad: string;
  email: string;
}

const GunSonuRaporu: React.FC = () => {
  const [ayarlar, setAyarlar] = useState<MailAyarlari>({
    smtpSunucu: '',
    smtpPort: 587,
    smtpKullanici: '',
    smtpSifreMasked: '',
    gonderenAd: 'YapiCRM',
    gonderenEposta: '',
    sslKullan: true,
    gonderimSaati: '18:00',
    aktifMi: false,
  });
  const [smtpSifre, setSmtpSifre] = useState('');
  const [sifredegisti, setSifredegisti] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [tumKullanicilar, setTumKullanicilar] = useState<Kullanici[]>([]);
  const [seciliAlicilar, setSeciliAlicilar] = useState<number[]>([]);
  const [alicilarSaving, setAlicilarSaving] = useState(false);

  const [testEposta, setTestEposta] = useState('');
  const [testGonderiliyor, setTestGonderiliyor] = useState(false);
  const [pdfIndiriliyor, setPdfIndiriliyor] = useState(false);

  const fetchAyarlar = useCallback(async () => {
    setLoading(true);
    try {
      const [ayarRes, aliciRes] = await Promise.all([
        axiosInstance.get('/api/GunSonuRaporu/ayarlar'),
        axiosInstance.get('/api/GunSonuRaporu/alicilar'),
      ]);
      setAyarlar(ayarRes.data);
      setTumKullanicilar(aliciRes.data.tumKullanicilar || []);
      setSeciliAlicilar((aliciRes.data.alicilar || []).map((a: Alici) => a.kullaniciID));
    } catch {
      toast.error('Ayarlar yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAyarlar();
  }, [fetchAyarlar]);

  const handleAyarlarKaydet = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axiosInstance.post('/api/GunSonuRaporu/ayarlar/update/1', {
        smtpSunucu: ayarlar.smtpSunucu,
        smtpPort: ayarlar.smtpPort,
        smtpKullanici: ayarlar.smtpKullanici,
        smtpSifre: sifredegisti ? smtpSifre : '',
        gonderenAd: ayarlar.gonderenAd,
        gonderenEposta: ayarlar.gonderenEposta,
        sslKullan: ayarlar.sslKullan,
        gonderimSaati: ayarlar.gonderimSaati,
        aktifMi: ayarlar.aktifMi,
      });
      toast.success('Mail ayarları kaydedildi.');
      setSifredegisti(false);
      setSmtpSifre('');
    } catch {
      toast.error('Kayıt sırasında hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  const handleAlicilarKaydet = async () => {
    setAlicilarSaving(true);
    try {
      await axiosInstance.post('/api/GunSonuRaporu/alicilar/update/1', {
        kullaniciIdler: seciliAlicilar,
      });
      toast.success('Alıcı listesi güncellendi.');
    } catch {
      toast.error('Alıcılar kaydedilemedi.');
    } finally {
      setAlicilarSaving(false);
    }
  };

  const handleTestGonder = async () => {
    if (!testEposta) { toast.warning('Test e-posta adresi girin.'); return; }
    setTestGonderiliyor(true);
    try {
      const res = await axiosInstance.post('/api/GunSonuRaporu/test-gonder', { hedefEposta: testEposta });
      toast.success(res.data.message || 'Test e-postası gönderildi.');
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Test gönderimi başarısız.';
      toast.error(msg);
    } finally {
      setTestGonderiliyor(false);
    }
  };

  const handlePdfIndir = async () => {
    setPdfIndiriliyor(true);
    try {
      const res = await axiosInstance.get('/api/GunSonuRaporu/pdf-indir', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      const tarih = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');
      link.href = url;
      link.setAttribute('download', `GunSonuRaporu_${tarih}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Rapor indirildi.');
    } catch (err: any) {
      let msg = 'PDF oluşturulamadı.';
      const blobData = err?.response?.data;
      if (blobData instanceof Blob) {
        try {
          const text = await blobData.text();
          const parsed = JSON.parse(text);
          msg = parsed.message || msg;
        } catch {
          // blob text parse edilemedi
        }
      }
      toast.error(msg);
    } finally {
      setPdfIndiriliyor(false);
    }
  };

  const toggleAlici = (id: number) => {
    setSeciliAlicilar(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  if (loading) {
    return (
      <div className="gsr-loading">
        <i className="fas fa-spinner fa-spin" /> Yükleniyor...
      </div>
    );
  }

  return (
    <div className="gsr-container">
      {/* ── Başlık ───────────────────────────────────────────────────────── */}
      <div className="gsr-header">
        <div className="gsr-header-icon">
          <i className="fas fa-envelope-open-text" />
        </div>
        <div>
          <div className="gsr-header-title">Gün Sonu Raporu</div>
          <div className="gsr-header-sub">
            Her gün belirlenen saatte seçili kullanıcılara otomatik e-posta ile gönderilir.
          </div>
        </div>
        <div className="gsr-aktif-badge">
          <button
            type="button"
            className="gsr-btn-indir"
            onClick={handlePdfIndir}
            disabled={pdfIndiriliyor}
            title="Bugünkü raporu PDF olarak indir"
          >
            {pdfIndiriliyor
              ? <><i className="fas fa-spinner fa-spin" /> Oluşturuluyor...</>
              : <><i className="fas fa-file-pdf" /> Raporu İndir</>}
          </button>
          <label className="gsr-toggle">
            <input
              type="checkbox"
              checked={ayarlar.aktifMi}
              onChange={e => setAyarlar(p => ({ ...p, aktifMi: e.target.checked }))}
            />
            <span className="gsr-toggle-slider" />
          </label>
          <span className={`gsr-aktif-label ${ayarlar.aktifMi ? 'aktif' : 'pasif'}`}>
            {ayarlar.aktifMi ? 'Aktif' : 'Pasif'}
          </span>
        </div>
      </div>

      {/* ── SMTP Ayarları ─────────────────────────────────────────────────── */}
      <div className="gsr-card">
        <div className="gsr-card-title">
          <i className="fas fa-server" /> SMTP Sunucu Ayarları
        </div>
        <form onSubmit={handleAyarlarKaydet} className="gsr-form">
          <div className="gsr-form-row">
            <div className="gsr-form-group">
              <label>SMTP Sunucu</label>
              <input
                type="text"
                className="gsr-input"
                placeholder="smtp.gmail.com"
                value={ayarlar.smtpSunucu}
                onChange={e => setAyarlar(p => ({ ...p, smtpSunucu: e.target.value }))}
              />
            </div>
            <div className="gsr-form-group gsr-form-group--sm">
              <label>Port</label>
              <input
                type="number"
                className="gsr-input"
                placeholder="587"
                value={ayarlar.smtpPort}
                onChange={e => setAyarlar(p => ({ ...p, smtpPort: Number(e.target.value) }))}
              />
            </div>
            <div className="gsr-form-group gsr-form-group--sm">
              <label>SSL/TLS</label>
              <select
                className="gsr-input"
                value={ayarlar.sslKullan ? '1' : '0'}
                onChange={e => setAyarlar(p => ({ ...p, sslKullan: e.target.value === '1' }))}
              >
                <option value="1">Kullan</option>
                <option value="0">Kullanma</option>
              </select>
            </div>
          </div>

          <div className="gsr-form-row">
            <div className="gsr-form-group">
              <label>Kullanıcı Adı</label>
              <input
                type="text"
                className="gsr-input"
                placeholder="eposta@domain.com"
                value={ayarlar.smtpKullanici}
                onChange={e => setAyarlar(p => ({ ...p, smtpKullanici: e.target.value }))}
              />
            </div>
            <div className="gsr-form-group">
              <label>Şifre</label>
              <input
                type="password"
                className="gsr-input"
                placeholder={ayarlar.smtpSifreMasked || 'Şifre giriniz'}
                value={smtpSifre}
                onChange={e => { setSmtpSifre(e.target.value); setSifredegisti(true); }}
              />
              {!sifredegisti && ayarlar.smtpSifreMasked && (
                <small className="gsr-input-hint">Değiştirmek istemiyorsanız boş bırakın</small>
              )}
            </div>
          </div>

          <div className="gsr-form-row">
            <div className="gsr-form-group">
              <label>Gönderen Ad</label>
              <input
                type="text"
                className="gsr-input"
                placeholder="YapiCRM"
                value={ayarlar.gonderenAd}
                onChange={e => setAyarlar(p => ({ ...p, gonderenAd: e.target.value }))}
              />
            </div>
            <div className="gsr-form-group">
              <label>Gönderen E-posta</label>
              <input
                type="email"
                className="gsr-input"
                placeholder="rapor@firma.com"
                value={ayarlar.gonderenEposta}
                onChange={e => setAyarlar(p => ({ ...p, gonderenEposta: e.target.value }))}
              />
            </div>
            <div className="gsr-form-group gsr-form-group--sm">
              <label>Gönderim Saati</label>
              <input
                type="time"
                className="gsr-input"
                value={ayarlar.gonderimSaati}
                onChange={e => setAyarlar(p => ({ ...p, gonderimSaati: e.target.value }))}
              />
            </div>
          </div>

          <div className="gsr-form-actions">
            <button type="submit" className="gsr-btn-primary" disabled={saving}>
              {saving ? <><i className="fas fa-spinner fa-spin" /> Kaydediliyor...</> : <><i className="fas fa-save" /> Ayarları Kaydet</>}
            </button>
          </div>
        </form>
      </div>

      {/* ── Test Gönder ───────────────────────────────────────────────────── */}
      <div className="gsr-card">
        <div className="gsr-card-title">
          <i className="fas fa-paper-plane" /> Test E-postası Gönder
        </div>
        <p className="gsr-card-desc">
          Mevcut ayarları test etmek için bir adrese deneme raporu gönderin. PDF eki dahil edilecektir.
        </p>
        <div className="gsr-test-row">
          <input
            type="email"
            className="gsr-input"
            placeholder="test@eposta.com"
            value={testEposta}
            onChange={e => setTestEposta(e.target.value)}
          />
          <button
            type="button"
            className="gsr-btn-secondary"
            onClick={handleTestGonder}
            disabled={testGonderiliyor}
          >
            {testGonderiliyor
              ? <><i className="fas fa-spinner fa-spin" /> Gönderiliyor...</>
              : <><i className="fas fa-vial" /> Test Gönder</>}
          </button>
        </div>
      </div>

      {/* ── Alıcı Listesi ─────────────────────────────────────────────────── */}
      <div className="gsr-card">
        <div className="gsr-card-title">
          <i className="fas fa-users" /> Rapor Alıcıları
        </div>
        <p className="gsr-card-desc">
          Gün sonu raporunu almak istediğiniz kullanıcıları seçin.
        </p>
        <div className="gsr-alici-list">
          {tumKullanicilar.length === 0 && (
            <div className="gsr-alici-empty">Sistemde kullanıcı bulunamadı.</div>
          )}
          {tumKullanicilar.map(k => (
            <label key={k.kullaniciID} className={`gsr-alici-item ${seciliAlicilar.includes(k.kullaniciID) ? 'secili' : ''}`}>
              <input
                type="checkbox"
                checked={seciliAlicilar.includes(k.kullaniciID)}
                onChange={() => toggleAlici(k.kullaniciID)}
              />
              <div className="gsr-alici-avatar">
                {k.adSoyad?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div className="gsr-alici-info">
                <div className="gsr-alici-ad">{k.adSoyad}</div>
                <div className="gsr-alici-eposta">{k.email}</div>
              </div>
              {seciliAlicilar.includes(k.kullaniciID) && (
                <i className="fas fa-check gsr-alici-check" />
              )}
            </label>
          ))}
        </div>
        <div className="gsr-form-actions">
          <span className="gsr-secili-sayisi">{seciliAlicilar.length} alıcı seçili</span>
          <button
            type="button"
            className="gsr-btn-primary"
            onClick={handleAlicilarKaydet}
            disabled={alicilarSaving}
          >
            {alicilarSaving
              ? <><i className="fas fa-spinner fa-spin" /> Kaydediliyor...</>
              : <><i className="fas fa-save" /> Alıcıları Kaydet</>}
          </button>
        </div>
      </div>

      <style>{`
        .gsr-loading {
          padding: 40px;
          color: #6b7a95;
          text-align: center;
          font-size: 14px;
        }
        .gsr-container {
          max-width: 800px;
        }
        /* Header */
        .gsr-header {
          display: flex;
          align-items: center;
          gap: 16px;
          background: #1a2440;
          border-radius: 12px;
          padding: 20px 24px;
          margin-bottom: 20px;
          color: #fff;
        }
        .gsr-header-icon {
          width: 48px; height: 48px;
          border-radius: 12px;
          background: rgba(230,125,33,0.2);
          display: flex; align-items: center; justify-content: center;
          font-size: 22px;
          color: #e67d21;
          flex-shrink: 0;
        }
        .gsr-header-title { font-size: 18px; font-weight: 700; }
        .gsr-header-sub { font-size: 12px; color: #aab4c8; margin-top: 3px; }
        .gsr-aktif-badge {
          margin-left: auto;
          display: flex; align-items: center; gap: 10px;
        }
        .gsr-btn-indir {
          background: rgba(230,125,33,0.15);
          color: #e67d21;
          border: 1.5px solid rgba(230,125,33,0.4);
          border-radius: 8px;
          padding: 8px 16px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          display: flex; align-items: center; gap: 7px;
          transition: background 0.2s, border-color 0.2s;
          white-space: nowrap;
        }
        .gsr-btn-indir:hover { background: #e67d21; color: #fff; border-color: #e67d21; }
        .gsr-btn-indir:disabled { opacity: 0.6; cursor: not-allowed; }
        .gsr-aktif-label { font-size: 13px; font-weight: 600; }
        .gsr-aktif-label.aktif { color: #4ade80; }
        .gsr-aktif-label.pasif { color: #aab4c8; }

        /* Toggle switch */
        .gsr-toggle { position: relative; display: inline-block; width: 44px; height: 24px; }
        .gsr-toggle input { opacity: 0; width: 0; height: 0; }
        .gsr-toggle-slider {
          position: absolute; cursor: pointer;
          top: 0; left: 0; right: 0; bottom: 0;
          background: #4b5a78; border-radius: 24px;
          transition: 0.3s;
        }
        .gsr-toggle-slider:before {
          position: absolute; content: '';
          height: 18px; width: 18px;
          left: 3px; bottom: 3px;
          background: #fff; border-radius: 50%;
          transition: 0.3s;
        }
        .gsr-toggle input:checked + .gsr-toggle-slider { background: #e67d21; }
        .gsr-toggle input:checked + .gsr-toggle-slider:before { transform: translateX(20px); }

        /* Cards */
        .gsr-card {
          background: #fff;
          border-radius: 12px;
          border: 1px solid #e8edf5;
          padding: 20px 24px;
          margin-bottom: 16px;
        }
        .gsr-card-title {
          font-size: 14px;
          font-weight: 700;
          color: #1a2440;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .gsr-card-title i { color: #e67d21; }
        .gsr-card-desc { font-size: 12px; color: #6b7a95; margin-bottom: 14px; }

        /* Form */
        .gsr-form-row {
          display: flex; gap: 12px; margin-bottom: 12px; flex-wrap: wrap;
        }
        .gsr-form-group {
          display: flex; flex-direction: column; gap: 5px; flex: 1; min-width: 160px;
        }
        .gsr-form-group--sm { flex: 0 0 110px; min-width: 100px; }
        .gsr-form-group label {
          font-size: 11px; font-weight: 600; color: #4b5a78; text-transform: uppercase; letter-spacing: 0.4px;
        }
        .gsr-input {
          border: 1.5px solid #dde3ee;
          border-radius: 8px;
          padding: 8px 12px;
          font-size: 13px;
          color: #1a2440;
          background: #f8faff;
          outline: none;
          transition: border-color 0.2s;
          width: 100%;
          box-sizing: border-box;
        }
        .gsr-input:focus { border-color: #e67d21; background: #fff; }
        .gsr-input-hint { font-size: 11px; color: #aab4c8; margin-top: 2px; }

        .gsr-form-actions {
          display: flex; align-items: center; justify-content: flex-end; gap: 12px; margin-top: 8px;
        }
        .gsr-secili-sayisi { font-size: 12px; color: #6b7a95; margin-right: auto; }

        .gsr-btn-primary {
          background: #1a2440;
          color: #fff;
          border: none;
          border-radius: 8px;
          padding: 9px 20px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          display: flex; align-items: center; gap: 7px;
          transition: background 0.2s;
        }
        .gsr-btn-primary:hover { background: #e67d21; }
        .gsr-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

        .gsr-btn-secondary {
          background: #f0f4f8;
          color: #1a2440;
          border: 1.5px solid #dde3ee;
          border-radius: 8px;
          padding: 9px 20px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          display: flex; align-items: center; gap: 7px;
          transition: background 0.2s;
          white-space: nowrap;
        }
        .gsr-btn-secondary:hover { background: #e67d21; color: #fff; border-color: #e67d21; }
        .gsr-btn-secondary:disabled { opacity: 0.6; cursor: not-allowed; }

        /* Test row */
        .gsr-test-row {
          display: flex; gap: 12px; align-items: center; flex-wrap: wrap;
        }
        .gsr-test-row .gsr-input { max-width: 320px; }

        /* Alıcı listesi */
        .gsr-alici-list {
          display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px;
        }
        .gsr-alici-empty { font-size: 13px; color: #aab4c8; padding: 12px 0; }
        .gsr-alici-item {
          display: flex; align-items: center; gap: 12px;
          padding: 10px 14px;
          border-radius: 8px;
          border: 1.5px solid #e8edf5;
          cursor: pointer;
          transition: border-color 0.2s, background 0.2s;
          background: #fff;
        }
        .gsr-alici-item:hover { border-color: #aab4c8; background: #f8faff; }
        .gsr-alici-item.secili { border-color: #e67d21; background: #fff8f2; }
        .gsr-alici-item input[type='checkbox'] { display: none; }
        .gsr-alici-avatar {
          width: 34px; height: 34px;
          border-radius: 50%;
          background: #1a2440;
          color: #fff;
          font-weight: 700; font-size: 14px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .gsr-alici-item.secili .gsr-alici-avatar { background: #e67d21; }
        .gsr-alici-info { flex: 1; }
        .gsr-alici-ad { font-size: 13px; font-weight: 600; color: #1a2440; }
        .gsr-alici-eposta { font-size: 11px; color: #6b7a95; }
        .gsr-alici-check { color: #e67d21; font-size: 13px; margin-left: auto; }
      `}</style>
    </div>
  );
};

export default GunSonuRaporu;
